// Pure view-model extraction for structured chat cards (plan todo 23).
// Every extractor is defensive: a malformed/missing tool result returns null
// so the router can fall back to the generic EstimateCard without crashing.
// Cards render tool-returned values only — no client-side nutrition math
// beyond defined-only-when-complete display aggregation (never zero-filled).

import { isRecord } from "../../lib/records";
import type { LoggingFlowReviewItem } from "../../lib/logging-flow";

export type ChatCardKind =
  | "meal-estimate"
  | "today-summary"
  | "weight-trend"
  | "goal-timeline"
  | "weekly-checkin"
  | "memory-result"
  | "data-quality";

/** Agent tool name -> structured card kind. Unmapped tools keep EstimateCard. */
export const TOOL_CARD_KINDS: Readonly<Record<string, ChatCardKind>> = {
  prepareMealReview: "meal-estimate",
  estimateFoodText: "meal-estimate",
  estimateFoodImage: "meal-estimate",
  getTodaySummary: "today-summary",
  getWeightTrend: "weight-trend",
  getGoalTimeline: "goal-timeline",
  // Reuses the goal-timeline-style card variant (with confidence) per the
  // todo-23 contract; the card branches on toolName for the expenditure view.
  getExpenditureInfo: "goal-timeline",
  getProgrammeAdjustments: "weekly-checkin",
  getPendingCheckin: "weekly-checkin",
  searchFoodMemory: "memory-result",
  listFoodMemories: "memory-result",
  saveFoodMemory: "memory-result",
  getDataQuality: "data-quality",
};

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

// ---------------------------------------------------------------------------
// Meal estimate (prepareMealReview / estimateFoodText / estimateFoodImage)
// ---------------------------------------------------------------------------

export interface MealEstimateItemView {
  readonly name: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly confidence?: string;
}

/** Defined only when EVERY item defines the nutrient (never zero-filled). */
export interface MealEstimateTotals {
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
}

export interface MealEstimateMemoryMatch {
  readonly foodName: string;
  readonly calories: number;
  readonly servingSize?: string;
  readonly confidence?: string;
}

export interface MealEstimateData {
  readonly variant: "items" | "memory" | "image";
  readonly mealName?: string;
  readonly items: readonly MealEstimateItemView[];
  readonly totals?: MealEstimateTotals;
  readonly uncertainty?: {
    readonly lowKcal: number;
    readonly highKcal: number;
    readonly reason?: string;
  };
  readonly memoryMatch?: MealEstimateMemoryMatch;
  readonly partialMatchCount: number;
  readonly imageUrl?: string;
  readonly accessible?: boolean;
}

function readItem(item: unknown): MealEstimateItemView | null {
  if (!isRecord(item)) return null;
  const name = readString(item.name);
  if (name === undefined) return null;
  return {
    name,
    ...(readNumber(item.quantity) !== undefined ? { quantity: readNumber(item.quantity) } : {}),
    ...(readString(item.unit) !== undefined ? { unit: readString(item.unit) } : {}),
    ...(readNumber(item.calories) !== undefined ? { calories: readNumber(item.calories) } : {}),
    ...(readNumber(item.proteinG) !== undefined ? { proteinG: readNumber(item.proteinG) } : {}),
    ...(readNumber(item.carbsG) !== undefined ? { carbsG: readNumber(item.carbsG) } : {}),
    ...(readNumber(item.fatG) !== undefined ? { fatG: readNumber(item.fatG) } : {}),
    ...(readNumber(item.fiberG) !== undefined ? { fiberG: readNumber(item.fiberG) } : {}),
    ...(readString(item.confidence) !== undefined ? { confidence: readString(item.confidence) } : {}),
  };
}

function sumWhenComplete(
  items: readonly MealEstimateItemView[],
  pick: (item: MealEstimateItemView) => number | undefined,
): number | undefined {
  let total = 0;
  for (const item of items) {
    const value = pick(item);
    if (value === undefined) return undefined;
    total += value;
  }
  return total;
}

export function extractMealEstimate(result: unknown): MealEstimateData | null {
  if (!isRecord(result)) return null;

  if (Array.isArray(result.items)) {
    if (result.items.length === 0) return null;
    const items: MealEstimateItemView[] = [];
    for (const raw of result.items) {
      const item = readItem(raw);
      if (item === null) return null;
      items.push(item);
    }
    const totals: MealEstimateTotals = {
      ...(sumWhenComplete(items, (item) => item.calories) !== undefined
        ? { calories: sumWhenComplete(items, (item) => item.calories) }
        : {}),
      ...(sumWhenComplete(items, (item) => item.proteinG) !== undefined
        ? { proteinG: sumWhenComplete(items, (item) => item.proteinG) }
        : {}),
      ...(sumWhenComplete(items, (item) => item.carbsG) !== undefined
        ? { carbsG: sumWhenComplete(items, (item) => item.carbsG) }
        : {}),
      ...(sumWhenComplete(items, (item) => item.fatG) !== undefined
        ? { fatG: sumWhenComplete(items, (item) => item.fatG) }
        : {}),
      ...(sumWhenComplete(items, (item) => item.fiberG) !== undefined
        ? { fiberG: sumWhenComplete(items, (item) => item.fiberG) }
        : {}),
    };
    const uncertainty = isRecord(result.uncertainty) ? result.uncertainty : undefined;
    const lowKcal = uncertainty !== undefined ? readNumber(uncertainty.lowKcal) : undefined;
    const highKcal = uncertainty !== undefined ? readNumber(uncertainty.highKcal) : undefined;
    const hasRange = uncertainty !== undefined && uncertainty.hasRange === true;
    return {
      variant: "items",
      ...(readString(result.mealName) !== undefined ? { mealName: readString(result.mealName) } : {}),
      items,
      totals,
      ...(hasRange && lowKcal !== undefined && highKcal !== undefined
        ? {
            uncertainty: {
              lowKcal,
              highKcal,
              ...(uncertainty !== undefined && readString(uncertainty.reason) !== undefined
                ? { reason: readString(uncertainty.reason) }
                : {}),
            },
          }
        : {}),
      partialMatchCount: 0,
    };
  }

  const memoryMatch = isRecord(result.memoryMatch) ? result.memoryMatch : undefined;
  if (
    memoryMatch !== undefined &&
    readString(memoryMatch.foodName) !== undefined &&
    readNumber(memoryMatch.calories) !== undefined
  ) {
    const partialMatchCount = Array.isArray(result.partialMatches)
      ? result.partialMatches.length
      : 0;
    return {
      variant: "memory",
      items: [],
      partialMatchCount,
      memoryMatch: {
        foodName: readString(memoryMatch.foodName) ?? "",
        calories: readNumber(memoryMatch.calories) ?? 0,
        ...(readString(memoryMatch.servingSize) !== undefined
          ? { servingSize: readString(memoryMatch.servingSize) }
          : {}),
        ...(readString(memoryMatch.confidence) !== undefined
          ? { confidence: readString(memoryMatch.confidence) }
          : {}),
      },
    };
  }

  if (typeof result.accessible === "boolean") {
    return {
      variant: "image",
      items: [],
      partialMatchCount: 0,
      accessible: result.accessible,
      ...(readString(result.imageUrl) !== undefined ? { imageUrl: readString(result.imageUrl) } : {}),
    };
  }

  return null;
}

/** Maps extracted estimate items onto the logging-flow review contract. */
export function buildReviewFlowItems(
  data: MealEstimateData,
): LoggingFlowReviewItem[] {
  return data.items.map((item) => ({
    name: item.name,
    ...(item.quantity !== undefined ? { quantity: item.quantity } : {}),
    ...(item.unit !== undefined ? { unit: item.unit } : {}),
    ...(item.calories !== undefined ? { calories: item.calories } : {}),
    ...(item.proteinG !== undefined ? { proteinG: item.proteinG } : {}),
    ...(item.carbsG !== undefined ? { carbsG: item.carbsG } : {}),
    ...(item.fatG !== undefined ? { fatG: item.fatG } : {}),
    ...(item.fiberG !== undefined ? { fiberG: item.fiberG } : {}),
    ...(item.confidence !== undefined ? { confidence: item.confidence } : {}),
  }));
}

// ---------------------------------------------------------------------------
// Today summary (getTodaySummary)
// ---------------------------------------------------------------------------

export interface TodaySummaryData {
  readonly consumedKcal: number;
  readonly goalKcal?: number;
  readonly remainingKcal?: number;
  readonly burnedKcal?: number;
  readonly consumedProteinG?: number;
  readonly consumedCarbsG?: number;
  readonly consumedFatG?: number;
  readonly consumedFiberG?: number;
  readonly goalProteinG?: number;
  readonly goalCarbsG?: number;
  readonly goalFatG?: number;
  readonly goalFiberG?: number;
}

export function extractTodaySummary(result: unknown): TodaySummaryData | null {
  if (!isRecord(result)) return null;
  const consumedKcal = readNumber(result.consumedKcal);
  if (consumedKcal === undefined) return null;
  return {
    consumedKcal,
    ...(readNumber(result.goalKcal) !== undefined ? { goalKcal: readNumber(result.goalKcal) } : {}),
    ...(readNumber(result.remainingKcal) !== undefined
      ? { remainingKcal: readNumber(result.remainingKcal) }
      : {}),
    ...(readNumber(result.burnedKcal) !== undefined
      ? { burnedKcal: readNumber(result.burnedKcal) }
      : {}),
    ...(readNumber(result.consumedProteinG) !== undefined
      ? { consumedProteinG: readNumber(result.consumedProteinG) }
      : {}),
    ...(readNumber(result.consumedCarbsG) !== undefined
      ? { consumedCarbsG: readNumber(result.consumedCarbsG) }
      : {}),
    ...(readNumber(result.consumedFatG) !== undefined
      ? { consumedFatG: readNumber(result.consumedFatG) }
      : {}),
    ...(readNumber(result.consumedFiberG) !== undefined
      ? { consumedFiberG: readNumber(result.consumedFiberG) }
      : {}),
    ...(readNumber(result.goalProteinG) !== undefined
      ? { goalProteinG: readNumber(result.goalProteinG) }
      : {}),
    ...(readNumber(result.goalCarbsG) !== undefined
      ? { goalCarbsG: readNumber(result.goalCarbsG) }
      : {}),
    ...(readNumber(result.goalFatG) !== undefined
      ? { goalFatG: readNumber(result.goalFatG) }
      : {}),
    ...(readNumber(result.goalFiberG) !== undefined
      ? { goalFiberG: readNumber(result.goalFiberG) }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Weight trend (getWeightTrend)
// ---------------------------------------------------------------------------

export interface WeightTrendData {
  readonly rawKg?: number;
  readonly trendKg?: number;
  readonly driftKgPerWeek?: number;
  readonly goalKg?: number;
  readonly trendConfidence?: string;
  readonly dataQuality?: string;
}

export function extractWeightTrend(result: unknown): WeightTrendData | null {
  if (!isRecord(result)) return null;
  const rawKg = readNumber(result.latestRawKg);
  const trendKg = readNumber(result.latestTrendKg);
  if (rawKg === undefined && trendKg === undefined) return null;
  return {
    ...(rawKg !== undefined ? { rawKg } : {}),
    ...(trendKg !== undefined ? { trendKg } : {}),
    ...(readNumber(result.driftKgPerWeek) !== undefined
      ? { driftKgPerWeek: readNumber(result.driftKgPerWeek) }
      : {}),
    ...(readNumber(result.goalKg) !== undefined ? { goalKg: readNumber(result.goalKg) } : {}),
    ...(readString(result.trendConfidence) !== undefined
      ? { trendConfidence: readString(result.trendConfidence) }
      : {}),
    ...(readString(result.dataQuality) !== undefined
      ? { dataQuality: readString(result.dataQuality) }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Goal timeline (getGoalTimeline) + expenditure variant (getExpenditureInfo)
// ---------------------------------------------------------------------------

export interface GoalTimelineData {
  readonly weeksToGoal?: number;
  readonly weeksMin?: number;
  readonly weeksMax?: number;
  readonly goalDateMin?: string;
  readonly goalDateMax?: string;
  readonly weeklyChangeKg?: number;
  readonly targetKcal?: number;
  readonly restricted?: boolean;
}

export function extractGoalTimeline(result: unknown): GoalTimelineData | null {
  if (!isRecord(result)) return null;
  const weeksToGoal = readNumber(result.weeksToGoal);
  const weeksMin = readNumber(result.weeksMin);
  const weeksMax = readNumber(result.weeksMax);
  const goalDateMin = readString(result.goalDateMin);
  const goalDateMax = readString(result.goalDateMax);
  const targetKcal = readNumber(result.targetKcal);
  if (
    weeksToGoal === undefined &&
    weeksMin === undefined &&
    goalDateMin === undefined &&
    targetKcal === undefined
  ) {
    return null;
  }
  return {
    ...(weeksToGoal !== undefined ? { weeksToGoal } : {}),
    ...(weeksMin !== undefined ? { weeksMin } : {}),
    ...(weeksMax !== undefined ? { weeksMax } : {}),
    ...(goalDateMin !== undefined ? { goalDateMin } : {}),
    ...(goalDateMax !== undefined ? { goalDateMax } : {}),
    ...(readNumber(result.weeklyChangeKg) !== undefined
      ? { weeklyChangeKg: readNumber(result.weeklyChangeKg) }
      : {}),
    ...(targetKcal !== undefined ? { targetKcal } : {}),
    ...(typeof result.restricted === "boolean" ? { restricted: result.restricted } : {}),
  };
}

export interface ExpenditureData {
  readonly expenditureKcal?: number;
  readonly kind?: string;
  readonly confidence?: string;
  readonly explanation?: string;
}

export function extractExpenditure(result: unknown): ExpenditureData | null {
  if (!isRecord(result)) return null;
  const expenditureKcal = readNumber(result.expenditureKcal);
  const explanation = readString(result.explanation);
  if (expenditureKcal === undefined && explanation === undefined) return null;
  return {
    ...(expenditureKcal !== undefined ? { expenditureKcal } : {}),
    ...(readString(result.kind) !== undefined ? { kind: readString(result.kind) } : {}),
    ...(readString(result.confidence) !== undefined
      ? { confidence: readString(result.confidence) }
      : {}),
    ...(explanation !== undefined ? { explanation } : {}),
  };
}

// ---------------------------------------------------------------------------
// Weekly check-in (getPendingCheckin / getProgrammeAdjustments)
// ---------------------------------------------------------------------------

export interface PendingCheckinView {
  readonly id: string;
  readonly previousTargetKcal: number;
  readonly newTargetKcal: number;
  readonly explanation: string;
  readonly reasonsJson: string;
}

export interface CheckinAdjustmentView {
  readonly previousTargetKcal: number;
  readonly newTargetKcal: number;
  readonly adjusted: boolean;
  readonly paused: boolean;
  readonly explanation?: string;
}

export interface WeeklyCheckinData {
  readonly pending: PendingCheckinView | null;
  readonly adjustments: readonly CheckinAdjustmentView[];
}

function readAdjustment(row: unknown): CheckinAdjustmentView | null {
  if (!isRecord(row)) return null;
  const previousTargetKcal = readNumber(row.previousTargetKcal);
  const newTargetKcal = readNumber(row.newTargetKcal);
  if (previousTargetKcal === undefined || newTargetKcal === undefined) return null;
  return {
    previousTargetKcal,
    newTargetKcal,
    adjusted: row.adjusted === true,
    paused: row.paused === true,
    ...(readString(row.explanation) !== undefined
      ? { explanation: readString(row.explanation) }
      : {}),
  };
}

export function extractWeeklyCheckin(
  toolName: string,
  result: unknown,
): WeeklyCheckinData | null {
  if (toolName === "getProgrammeAdjustments") {
    if (!Array.isArray(result)) return null;
    const adjustments: CheckinAdjustmentView[] = [];
    for (const row of result) {
      const adjustment = readAdjustment(row);
      if (adjustment !== null) adjustments.push(adjustment);
    }
    return { pending: null, adjustments };
  }

  // getPendingCheckin: null means no proposal is waiting.
  if (result === null) return { pending: null, adjustments: [] };
  if (!isRecord(result)) return null;
  const previousTargetKcal = readNumber(result.previousTargetKcal);
  const newTargetKcal = readNumber(result.newTargetKcal);
  if (previousTargetKcal === undefined || newTargetKcal === undefined) return null;
  if (result.status === "pending" && typeof result._id === "string") {
    return {
      pending: {
        id: result._id,
        previousTargetKcal,
        newTargetKcal,
        explanation: typeof result.explanation === "string" ? result.explanation : "",
        reasonsJson: typeof result.reasonsJson === "string" ? result.reasonsJson : "[]",
      },
      adjustments: [],
    };
  }
  const adjustment = readAdjustment(result);
  return { pending: null, adjustments: adjustment !== null ? [adjustment] : [] };
}

// ---------------------------------------------------------------------------
// Memory result (searchFoodMemory / listFoodMemories / saveFoodMemory)
// ---------------------------------------------------------------------------

export interface MemoryResultItem {
  readonly id?: string;
  readonly foodName: string;
  readonly calories: number;
  readonly servingSize?: string;
  readonly confidence?: string;
  readonly source?: string;
}

export interface MemoryResultData {
  readonly memories: readonly MemoryResultItem[];
}

function readMemory(row: unknown): MemoryResultItem | null {
  if (!isRecord(row)) return null;
  const foodName = readString(row.foodName);
  const calories = readNumber(row.calories);
  if (foodName === undefined || calories === undefined) return null;
  return {
    ...(typeof row._id === "string" ? { id: row._id } : {}),
    foodName,
    calories,
    ...(readString(row.servingSize) !== undefined
      ? { servingSize: readString(row.servingSize) }
      : {}),
    ...(readString(row.confidence) !== undefined
      ? { confidence: readString(row.confidence) }
      : {}),
    ...(readString(row.source) !== undefined ? { source: readString(row.source) } : {}),
  };
}

export function extractMemoryResult(result: unknown): MemoryResultData | null {
  if (Array.isArray(result)) {
    const memories: MemoryResultItem[] = [];
    for (const row of result) {
      const memory = readMemory(row);
      if (memory !== null) memories.push(memory);
    }
    return { memories };
  }
  const single = readMemory(result);
  if (single !== null) return { memories: [single] };
  if (isRecord(result)) {
    const nested = readMemory(result.memory);
    if (nested !== null) return { memories: [nested] };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Data quality (getDataQuality)
// ---------------------------------------------------------------------------

export interface DataQualityData {
  readonly dataQuality: string;
  readonly trendConfidence?: string;
  readonly expenditureConfidence?: string;
  readonly actions: readonly string[];
}

export function extractDataQuality(result: unknown): DataQualityData | null {
  if (!isRecord(result)) return null;
  const dataQuality = readString(result.dataQuality);
  if (dataQuality === undefined) return null;
  const actions = Array.isArray(result.actions)
    ? result.actions.filter((action): action is string => typeof action === "string")
    : [];
  return {
    dataQuality,
    ...(readString(result.trendConfidence) !== undefined
      ? { trendConfidence: readString(result.trendConfidence) }
      : {}),
    ...(readString(result.expenditureConfidence) !== undefined
      ? { expenditureConfidence: readString(result.expenditureConfidence) }
      : {}),
    actions,
  };
}
